import { GridApiPro, GridRowId, GridRowModelUpdate } from '@mui/x-data-grid-pro';
import { differenceInCalendarDays, format, isDate, isValid, parseISO } from 'date-fns';
import Cookies from 'js-cookie';
import uniqueId from 'lodash.uniqueid';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { BENCHMARK_GAP_HINT_DISMISSED, REGULATION_ATTRIBUTE_TYPE } from '../constants';
import { Answer, Questions, Sections } from '../interfaces/Participants';

export interface ChecklistData {
  answers?: Answer[];
  sections?: Sections[];
}

export class Utils {
  static formatDate(s: string | Date | number, type?: 'long' | 'short'): string {
    const date = Date.parse(String(s));
    if (isNaN(date)) return '';

    if (Utils.isToday(new Date(date))) return 'Today';

    const opts: Intl.DateTimeFormatOptions =
      type === 'long'
        ? {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          }
        : {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          };

    return new Intl.DateTimeFormat('en-GB', opts).format(date);
  }
  static formatDateWithoutToday(s: string | Date | number, type?: 'long' | 'short'): string {
    const date = Date.parse(String(s));
    if (isNaN(date)) return '';

    const opts: Intl.DateTimeFormatOptions =
      type === 'long'
        ? {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          }
        : {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          };

    return new Intl.DateTimeFormat('en-GB', opts).format(date);
  }

  static isToday(date: Date | number): boolean {
    const dateObj = typeof date === 'number' ? new Date(date) : date;
    if (typeof dateObj.getDate !== 'function') return false;
    const today = new Date();
    return (
      dateObj.getDate() === today.getDate() &&
      dateObj.getMonth() === today.getMonth() &&
      dateObj.getFullYear() === today.getFullYear()
    );
  }

  static toApiDate(date: Date | null | undefined): string | null {
    if (!date) return null;
    const offset = date.getTimezoneOffset();
    const d = new Date(date.getTime() - offset * 60 * 1000);
    return d.toISOString().split('T')[0];
  }

  static formatReadableDate(date: Date | null | undefined): string | null {
    if (!date) return null;
    const yyyy = date.getFullYear().toString();
    const mm = (date.getMonth() + 1).toString();
    const dd = date.getDate().toString();

    return dd + '/' + mm + '/' + yyyy;
  }

  static parseApiDate(date: string | null | undefined): Date | string | null | undefined {
    return date ? new Date(Date.parse(date)) : date;
  }

  static formatProficiency(prof: number | null | undefined, valid?: boolean): string {
    return prof && prof > 0 && (valid === undefined || valid) ? Utils.formatNumber(prof) : 'n/a';
  }

  static formatProficiencyFraction(
    currentProf: number | null | undefined,
    requiredProf: number | null | undefined,
    valid?: boolean
  ): string {
    return currentProf &&
      currentProf > 0 &&
      requiredProf &&
      requiredProf > 0 &&
      (valid === undefined || valid)
      ? Utils.formatProficiency(currentProf, valid) +
          ' / ' +
          Utils.formatProficiency(requiredProf, valid)
      : 'n/a';
  }

  static formatPercent(n: number | null): string {
    if (n === null || isNaN(n)) return 'n/a';
    return Math.ceil(n) + '%';
  }

  static roundNumber(num: number, dec = 2): number {
    return +(Math.round(Number(num + 'e+' + dec)) + 'e-' + dec);
  }

  static zeroPadNumber(num: number | string, zeros: number): string {
    return String(num).padStart(zeros, '0');
  }

  static formatNumber(num: number): string {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  static formatCurrency(num: number | string, dec = 0): string {
    return parseInt(String(num), 10).toLocaleString('en-US', { minimumFractionDigits: dec });
  }

  static setCookie(name: string, value: string, days = 7, path = '/'): void {
    const expires =
      days === -1
        ? 'Thu, 01 Jan 1970 00:00:00 GMT'
        : new Date(Date.now() + days * 864e5).toUTCString();
    // Cookie setting commented out - see original comments for options
    void expires;
    void name;
    void value;
    void path;
  }

  static getCookie(name: string): string {
    return document.cookie.split('; ').reduce((r, v) => {
      const parts = v.split('=');
      return parts[0] === name ? decodeURIComponent(parts[1]) : r;
    }, '');
  }

  static truncate(input: string | null | undefined, limit: number): string | null | undefined {
    if (!input) return input;
    return input.length > limit ? input.substring(0, limit) + '...' : input;
  }

  static deleteCookie(name: string, path: string): void {
    Cookies.remove(name, { path, sameSite: 'none' });
  }

  static getDevOption(key: string): boolean {
    return window.localStorage.getItem(key) === 'true';
  }

  static setDevOption(key: string, value: string): void {
    window.localStorage.setItem(key, value);
  }

  static getLocalStorageOption(key: string): string | null {
    return window.localStorage.getItem(key);
  }

  static checkLocalStorageOption(key: string): boolean {
    return window.localStorage.getItem(key) === 'true';
  }

  static setLocalStorageOption(key: string, value: string): void {
    window.localStorage.setItem(key, value);
  }

  // Clears per-login-session UI flags so they reset on the next login. Called on logout and on
  // token-expiry re-auth redirects.
  static clearLoginSessionUiFlags(): void {
    window.localStorage.removeItem(BENCHMARK_GAP_HINT_DISMISSED);
  }

  static removeLocationHash(): void {
    window.history.pushState('', document.title, window.location.pathname + window.location.search);
  }

  static setLocation(
    history: { push: (path: string) => void },
    path: string,
    useNative?: boolean
  ): void {
    if (useNative === true) window.history.pushState(null, '', path);
    else history.push(path);
  }

  static isValidEmail(email: string | null | undefined): boolean {
    return email
      ? email.match(
          // eslint-disable-next-line no-useless-escape
          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        ) !== null
      : false;
  }

  static isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  static generateUniqueId(prefix?: string): string {
    return uniqueId(prefix);
  }

  static convertPracticalTime(time: string | null | undefined, at?: boolean): string {
    if (!time) return '';
    const splits = String(time).trim().split(' ');
    const suffix = splits[1].toLowerCase();
    const timeSegments = splits[0].split(':');
    const hour = timeSegments[0];
    const minute = this.zeroPadNumber(timeSegments[1], 2);
    return `${at ? 'at ' : ''}${hour}:${minute} ${suffix}`;
  }

  static formatHoursToHoursMinutes(hours: number | string | null | undefined): string {
    if (!hours || hours === '') return '';
    const hoursNum = typeof hours === 'string' ? parseFloat(hours) : hours;
    if (isNaN(hoursNum) || hoursNum < 0) return '';

    const wholeHours = Math.floor(hoursNum);
    const minutes = Math.round((hoursNum - wholeHours) * 60);
    const parts: string[] = [];

    if (wholeHours > 0) parts.push(`${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);

    return parts.join(' ');
  }

  static toDtoDate(date: Date | string | number | null | undefined): string | null {
    if (!date) return null;
    return format(new Date(date), "yyyy-MM-dd'T'00:00:00");
  }

  // Tree grid row types for MUI DataGrid
  static getTreeDataPath = (row: GridRowModelUpdate): string[] => row.hierarchy;

  static getRowId = (row: GridRowModelUpdate): string => {
    if (typeof row?.id === 'string' && row?.id.startsWith('placeholder-children-')) {
      return row.id;
    }
    return row.id;
  };

  static updateRows(apiRef: React.MutableRefObject<GridApiPro>, rows: GridRowModelUpdate[]): void {
    if (!apiRef.current) {
      return;
    }
    const rowsToAdd = [...rows];
    rows.forEach((row) => {
      if (row.descendantCount && row.descendantCount > 0) {
        rowsToAdd.push({
          id: `placeholder-children-${this.getRowId(row)}`,
          hierarchy: [...row.hierarchy, ''],
        });
      }
    });
    apiRef.current.updateRows(rowsToAdd);
  }

  static updateNewCategoryRowAndParent(
    apiRef: React.MutableRefObject<GridApiPro>,
    row: GridRowModelUpdate,
    parentRow: GridRowModelUpdate
  ): void {
    if (!apiRef.current) {
      return;
    }

    const rowsToAdd = [row];

    if (parentRow.descendantCount && parentRow.descendantCount > 0 && !parentRow.childrenFetched) {
      rowsToAdd.push({
        id: `placeholder-children-${this.getRowId(parentRow)}`,
        hierarchy: [...parentRow.hierarchy, ''],
      });
    }
    apiRef.current.updateRows(rowsToAdd);
  }

  static updateDomainRow(
    apiRef: React.MutableRefObject<GridApiPro>,
    row: GridRowModelUpdate,
    callback: () => void
  ): void {
    if (!apiRef.current) {
      return;
    }

    const rowIds = apiRef.current.getAllRowIds();
    const allRows = rowIds.map((rowId) => apiRef.current!.getRow(rowId));
    const allRowNodes = rowIds.map((rowId) => apiRef.current!.getRowNode(rowId));
    const selectedRowNode = allRowNodes.find((node) => node?.id === row.id);

    if (selectedRowNode?.type === 'leaf') {
      const updatedRows = allRows.map((r) => {
        if (r.id === row.id) {
          return row;
        }
        return r;
      });
      apiRef.current.updateRows(updatedRows);
    }
    if (selectedRowNode?.type === 'group') {
      callback();
    }
  }

  static deleteRow(
    apiRef: React.MutableRefObject<GridApiPro>,
    rowId: string,
    type: string | number
  ): void {
    if (!apiRef.current) {
      return;
    }

    const rowIds = apiRef.current.getAllRowIds();
    const allRows = rowIds.map((id) => {
      const row = apiRef.current!.getRow(id);
      return row;
    });

    const allRowNodes = rowIds.map((id) => {
      const row = apiRef.current!.getRowNode(id);
      return row;
    });

    if (Number(type) === REGULATION_ATTRIBUTE_TYPE.DOMAIN) {
      const rowsToAdd = allRows.map((row) => {
        if (row.id === rowId) {
          return { ...row, _action: 'delete' };
        }
        if (row.id === `placeholder-children-${rowId}`) {
          return { ...row, _action: 'delete' };
        }
        return row;
      });
      apiRef.current.updateRows(rowsToAdd);
    } else if (Number(type) === REGULATION_ATTRIBUTE_TYPE.CATEGORY) {
      const rowsToAdd = allRows.map((row) => {
        if (row.id === rowId) {
          return { ...row, _action: 'delete' };
        }
        if (
          row.id === `placeholder-children-${rowId}` ||
          get(row, 'regulationCategory.id', null) === rowId
        ) {
          return { ...row, _action: 'delete' };
        }
        return row;
      });

      const parentRowNode = allRowNodes.find((node) => node?.id === rowId);
      const parentRow = apiRef.current.getRow(parentRowNode?.parent || '');

      // Decrease parent descendant count with allRowNodes
      const parentRowDescendantCount = (parentRow.descendantCount || 0) - 1;
      const parentRowToUpdate = { ...parentRow, descendantCount: parentRowDescendantCount };
      const parentRowToUpdateIndex = allRows.findIndex((row) => row.id === parentRow.id);
      rowsToAdd[parentRowToUpdateIndex] = parentRowToUpdate;

      apiRef.current.updateRows(rowsToAdd);
    } else {
      const rowsToAdd = allRows.map((row) => {
        if (row.id === rowId) {
          return { ...row, _action: 'delete' };
        }
        return row;
      });

      const parentRowNode = allRowNodes.find((node) => node?.id === rowId);

      const parentRow = apiRef.current.getRow(parentRowNode?.parent || '');

      // Decrease parent descendant count with allRowNodes
      const parentRowDescendantCount = (parentRow.descendantCount || 0) - 1;
      const parentRowToUpdate = { ...parentRow, descendantCount: parentRowDescendantCount };
      const parentRowToUpdateIndex = allRows.findIndex((row) => row.id === parentRow.id);
      rowsToAdd[parentRowToUpdateIndex] = parentRowToUpdate;
      apiRef.current.updateRows(rowsToAdd);
    }
  }

  static getAllRowIds(apiRef: React.MutableRefObject<GridApiPro>): GridRowId[] {
    if (isEmpty(apiRef.current)) {
      return [];
    }

    return apiRef.current.getAllRowIds();
  }
  static checkAllAnswersComplete(answers: Answer[]) {
    if (!answers || answers.length === 0) {
      return false;
    }

    return answers.every((answer: Answer) => {
      return (
        (Array.isArray(answer.selectedOptionIds) && answer.selectedOptionIds.length > 0) ||
        !!answer.dateAnswer ||
        !!answer.textAnswer
      );
    });
  }

  static isExpiryDateValid(expiryDate: Date | string | number) {
    const normalized = isDate(expiryDate) ? expiryDate : parseISO(String(expiryDate));
    const isValidDate = isValid(normalized);

    return isValidDate;
  }

  static getAnswersFromChecklist = (data: ChecklistData | null | undefined): Answer[] => {
    if (data?.answers) {
      return data.answers;
    }
    const allQuestions = data?.sections?.flatMap((section: Sections) => section?.questions) || [];
    const answers: Answer[] = allQuestions.map((question: Questions) => {
      const answer = question?.answer || {};
      return {
        questionId: question?.id,
        selectedOptionIds: answer.selectedOptionIds || [],
        dateAnswer: answer.dateAnswer || '',
        textAnswer: answer.textAnswer || '',
      };
    });

    const hasAnyAnswer = answers.some(
      (answer: Answer) =>
        (Array.isArray(answer.selectedOptionIds) && answer.selectedOptionIds.length > 0) ||
        !!answer.dateAnswer ||
        !!answer.textAnswer
    );

    return hasAnyAnswer ? answers : [];
  };

  /**
   * Whole calendar days from date1 to date2 (negative when date2 is earlier).
   *
   * This used to subtract two local midnights and divide by 86_400_000, which is not a whole number of
   * days when a DST transition falls between them — the two midnights sit at different UTC offsets and
   * leave a one-hour remainder. That shipped as "Overdue by 1257.0416666666667 days" (0.041666… = 1/24).
   * date-fns compensates the offset instead of rounding it away, so the arithmetic cannot drift again.
   * Kept as a wrapper because date-fns takes (later, earlier) and eight call sites rely on this order.
   */
  static getDaysBetweenDates(date1: Date, date2: Date): number {
    return differenceInCalendarDays(date2, date1);
  }
}
